import { makeStyles, Theme } from '@material-ui/core/styles'

import {
    AXIS_HEIGHT,
    LEFT_COLUMN_WIDTH,
    SERVICE_ROW_HEIGHT,
    VENDOR_ROW_HEIGHT,
} from './constants'

export const useWalkScheduleStyles = makeStyles((theme: Theme) => ({
    root: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.grey[100],
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        minHeight: 52,
        padding: theme.spacing(1, 2),
        flexShrink: 0,
    },
    stats: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    stat: {
        height: 36,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0, 1.5),
        borderRadius: 8,
        border: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: theme.palette.background.paper,
        whiteSpace: 'nowrap',
    },
    statLabel: {
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    statValue: {
        fontWeight: 700,
        fontSize: 16,
    },
    leading: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
        flexWrap: 'wrap',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        flexShrink: 0,
    },
    banner: {
        margin: theme.spacing(0, 2, 1),
        padding: theme.spacing(0.75, 1.5),
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.warning.light}`,
        color: theme.palette.text.primary,
        fontSize: 13,
        flexShrink: 0,
    },
    body: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    pickerWrap: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        overflow: 'auto',
        padding: theme.spacing(2),
    },
    pickerCard: {
        width: 'auto',
        maxWidth: '100%',
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: `1px solid ${theme.palette.grey[300]}`,
        padding: theme.spacing(2, 2, 2.5),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    pickerTitle: {
        fontWeight: 700,
        alignSelf: 'stretch',
    },
    pickerHint: {
        alignSelf: 'stretch',
        marginBottom: theme.spacing(1),
    },
    fieldRow: {
        display: 'flex',
        gap: theme.spacing(1),
        alignSelf: 'stretch',
        marginBottom: theme.spacing(1),
    },
    fieldButton: {
        flex: 1,
        textAlign: 'left',
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 8,
        background: 'transparent',
        padding: theme.spacing(1, 1.5),
        cursor: 'pointer',
        fontFamily: 'inherit',
        '&:disabled': {
            cursor: 'default',
            opacity: 0.6,
        },
    },
    fieldButtonActive: {
        borderColor: theme.palette.primary.main,
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    },
    fieldLabel: {
        display: 'block',
        fontSize: 11,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    fieldValue: {
        display: 'block',
        fontSize: 14,
        fontWeight: 600,
        marginTop: 2,
        color: theme.palette.text.primary,
    },
    calendarRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: theme.spacing(1),
        width: '100%',
    },
    monthBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    monthTitle: {
        height: 40,
        display: 'flex',
        alignItems: 'center',
        fontWeight: 600,
    },
    weekdayRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 36px)',
        marginBottom: theme.spacing(0.5),
    },
    weekday: {
        width: 36,
        textAlign: 'center',
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    monthGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 36px)',
    },
    dayCell: {
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    dayButton: {
        width: 36,
        height: 36,
        border: 0,
        borderRadius: '50%',
        background: 'transparent',
        padding: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: theme.typography.caption.fontSize,
        fontWeight: 500,
        color: theme.palette.text.primary,
        '&:disabled': {
            cursor: 'default',
            color: theme.palette.text.disabled,
        },
    },
    dayCount: {
        fontWeight: 700,
        marginTop: theme.spacing(1),
    },
    pickerError: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(0.5),
        textAlign: 'center',
    },
    pickerActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        alignSelf: 'stretch',
        marginTop: theme.spacing(1.5),
    },
    rangeDay: {
        backgroundColor: 'rgba(46, 125, 50, 0.12)',
    },
    rangeEdge: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: '50%',
    },
    scroller: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        display: 'flex',
        alignItems: 'flex-start',
        position: 'relative',
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.grey[300]}`,
    },
    leftColumn: {
        position: 'sticky',
        left: 0,
        zIndex: 2,
        width: LEFT_COLUMN_WIDTH,
        minWidth: LEFT_COLUMN_WIDTH,
        flexShrink: 0,
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.grey[300]}`,
    },
    corner: {
        position: 'sticky',
        top: 0,
        zIndex: 4,
        height: AXIS_HEIGHT,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0, 1.5),
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        fontWeight: 700,
        fontSize: 13,
    },
    serviceRow: {
        height: SERVICE_ROW_HEIGHT,
        boxSizing: 'border-box',
        padding: theme.spacing(0.5, 1),
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 2,
        overflow: 'hidden',
    },
    serviceRowMain: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    serviceName: {
        fontWeight: 700,
        fontSize: 15,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
        minWidth: 0,
    },
    serviceMeta: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    serviceCounts: {
        fontSize: 14.5,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '& strong': {
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
    },
    actionsButton: {
        textTransform: 'none',
        flexShrink: 0,
        padding: theme.spacing(0.25, 1),
    },
    moneyRow: {
        display: 'flex',
        alignItems: 'stretch',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },
    moneyBlock: {
        flex: 1,
        minWidth: 0,
        padding: theme.spacing(0.25, 0.75),
        borderRadius: 8,
        border: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: theme.palette.background.paper,
    },
    moneyLabel: {
        fontSize: 14.5,
        fontWeight: 400,
        color: theme.palette.text.secondary,
        lineHeight: 1.15,
    },
    moneyValue: {
        fontSize: 14.5,
        fontWeight: 700,
        lineHeight: 1.15,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    vendorRow: {
        height: VENDOR_ROW_HEIGHT,
        boxSizing: 'border-box',
        padding: theme.spacing(0, 0.5, 0, 2),
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        overflow: 'hidden',
    },
    vendorName: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: 600,
        fontSize: 14,
    },
    vendorMeta: {
        fontSize: 14.5,
        fontWeight: 700,
        color: theme.palette.text.primary,
        flexShrink: 0,
    },
    chart: {
        position: 'relative',
        flexShrink: 0,
    },
    axis: {
        position: 'sticky',
        top: 0,
        zIndex: 3,
        height: AXIS_HEIGHT,
        display: 'flex',
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
    },
    dayHeader: {
        boxSizing: 'border-box',
        height: AXIS_HEIGHT,
        margin: 0,
        border: 'none',
        borderLeft: `1px solid ${theme.palette.grey[200]}`,
        background: 'transparent',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        lineHeight: 1.1,
        font: 'inherit',
        color: 'inherit',
    },
    dayWeek: {
        fontSize: 10,
        color: theme.palette.text.secondary,
    },
    dayNumber: {
        fontSize: 13,
        fontWeight: 700,
    },
    dayDots: {
        display: 'flex',
        gap: 2,
        height: 6,
        alignItems: 'center',
    },
    chartBody: {
        position: 'relative',
    },
    column: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        boxSizing: 'border-box',
        borderLeft: `1px solid ${theme.palette.grey[200]}`,
        pointerEvents: 'none',
    },
    bar: {
        position: 'absolute',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'stretch',
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    barHandle: {
        width: 10,
        flexShrink: 0,
        cursor: 'ew-resize',
        backgroundColor: 'rgba(255,255,255,0.35)',
    },
    barDateButton: {
        position: 'absolute',
        top: -7,
        zIndex: 3,
        width: 18,
        height: 18,
        padding: 0,
        border: `1px solid ${theme.palette.grey[400]}`,
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    workFill: {
        position: 'absolute',
        top: 4,
        bottom: 4,
        borderRadius: 3,
        backgroundColor: 'rgba(0,0,0,0.18)',
        color: theme.palette.common.white,
        fontSize: 10,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
    },
    emptyChart: {
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
    },
    serviceSpacer: {
        height: SERVICE_ROW_HEIGHT,
        boxSizing: 'border-box',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    vendorSpacer: {
        height: VENDOR_ROW_HEIGHT,
        boxSizing: 'border-box',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
}))
